import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

const customConfig = defineConfig({
  cssVarsPrefix: 'container-app',
  theme: {
    tokens: {
      shadows: {
        'card-action': { value: '0px 2px 2px rgba(255, 255, 255, 0.2)' },
        'raised': { value: '0px 8px 16px rgba(17, 17, 17, 0.4)' },
        // Two-layer drop-shadow for the Notification Bell drawer shell (#805025), exact Figma
        // value — no existing shadow token matches (closest, `raised`, is a single-layer shadow
        // with a very different spread/opacity).
        'panel': {
          value: '0px 4px 3px rgba(0, 0, 0, 0.1), 0px 2px 2px rgba(0, 0, 0, 0.1)',
        },
      },
      colors: {
        yellow: {
          400: { value: '#FDCA70' },
        },
        gray: {
          300: { value: '#D8D8DC' },
          350: { value: '#E4E4E7' },
          400: { value: '#B6B6B9' },
          450: { value: '#717182' },
          500: { value: '#737373' },
          600: { value: '#5C5C5C' },
          700: { value: '#2E2E2E' },
          800: { value: '#2D3748' },
          900: { value: '#1A202C' },
        },
        blue: {
          50: { value: '#E6F4FF' },
          75: { value: '#D7E4FE' },
          100: { value: '#F4F7FE' },
          150: { value: '#BFDBFE' },
          200: { value: '#ECEFF7' },
          500: { value: '#283897' },
          600: { value: '#173DA6' },
        },
        green: {
          200: { value: '#D3F1E0' },
          400: { value: '#34A866' },
          600: { value: '#1F653D' },
        },
        orange: {
          100: { value: '#FEEFD3' },
          400: { value: '#BD8E39' },
          500: { value: '#ef661a' },
        },
        red: {
          100: { value: '#F8C6C6' },
          200: { value: '#DA2020' },
          500: { value: '#831313' },
        },
        whiteAlpha: {
          400: { value: 'rgba(255, 255, 255, 0.8)' },
          500: { value: '#FFFFFF' },
        },
        blackAlpha: {
          800: { value: 'rgba(17, 17, 17, 0.9)' },
          900: { value: '#111111' },
        },
        slate: {
          200: { value: '#C8CEDC' },
          300: { value: '#8A94AB' },
          400: { value: '#677089' },
          500: { value: '#505874' },
          700: { value: '#39405A' },
          800: { value: '#1F2537' },
        },
        scrollbar: {
          track: { value: '#E8E8E8' },
          thumb: { value: '#7A7A7A' },
          thumbHover: { value: '#5A5A5A' },
        },
      },
      radii: {
        'radius-xl': { value: '32px' },
      },
      fonts: {
        body: {
          value:
            'var(--app-font-family, inherit), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        },
        heading: {
          value:
            'var(--app-font-family, inherit), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        },
      },
    },
    semanticTokens: {
      radii: {
        'card-xl': { value: '{radii.radius-xl}' },
      },
      colors: {
        text: {
          primary: { value: '{colors.blackAlpha.800}' },
          description: { value: '{colors.gray.450}' },
          dim: { value: 'rgba(0,0,0,0.25)' },
        },
        brand: {
          'primary': { value: '{colors.blue.600}' },
          'highlight': { value: '{colors.blue.500}' },
          // Exact Figma bell-icon stroke color (#805025) — distinct from `brand.primary`
          // (`blue.600` = #173DA6), which is a close-but-different existing blue.
          'primary-pressed': { value: '#1A3478' },
        },
        border: {
          default: { value: '{colors.gray.350}' },
          solid: { value: '{colors.gray.500}' },
          subtle: { value: '{colors.gray.400}' },
          error: { value: '{colors.red.200}' },
        },
        fg: {
          error: { value: '{colors.red.200}' },
        },
        yellow: {
          solid: { value: '{colors.yellow.400}' },
        },
        gray: {
          pale: { value: '{colors.gray.300}' },
          subtle: { value: '{colors.gray.400}' },
          neutral: { value: '{colors.gray.500}' },
          muted: { value: '{colors.gray.600}' },
          medium: { value: '{colors.gray.700}' },
          dark: { value: '{colors.gray.800}' },
          darkest: { value: '{colors.gray.900}' },
        },
        whiteAlpha: {
          solid: { value: '{colors.whiteAlpha.500}' },
          subtle: { value: '{colors.whiteAlpha.400}' },
        },
        blackAlpha: {
          strong: { value: '{colors.blackAlpha.800}' },
          solid: { value: '{colors.blackAlpha.900}' },
        },
        background: {
          default: { value: '{colors.blue.100}' },
        },
        errorAlert: {
          solid: { value: '{colors.red.200}' },
        },
        green: {
          muted: { value: '{colors.green.200}' },
          pressed: { value: '{colors.green.600}' },
        },
        orange: {
          muted: { value: '{colors.orange.100}' },
          pressed: { value: '{colors.orange.400}' },
          strong: { value: '#A74712' },
          soft: { value: '#FBD9C6' },
        },
        red: {
          muted: { value: '{colors.red.100}' },
          pressed: { value: '{colors.red.500}' },
          strong: { value: '#AE1A1A' },
          soft: { value: '#EB7777' },
        },
        button: {
          disabled: { value: '{colors.slate.200}' },
        },
        info: {
          bg: { value: '{colors.blue.50}' },
          border: { value: '{colors.blue.150}' },
          // Notification Bell icon-pill background (#805025) — Figma "Color/blue/100" (#D7E4FE)
          // is a NAMING COLLISION with this project's own `blue.100` (#F4F7FE, a different hex).
          // Map to `blue.75` (already an exact #D7E4FE match) via this semantic name instead —
          // never reference `blue.100` for this value.
          subtle: { value: '{colors.blue.75}' },
          strong: { value: '#1375A4' },
          soft: { value: '#A3D7F0' },
        },
        tooltip: {
          bg: { value: '#14204A' },
          fg: { value: '{colors.whiteAlpha.500}' },
        },
        select: {
          border: { value: '{colors.slate.500}' },
          text: { value: '{colors.slate.700}' },
          selected: { value: '{colors.blue.150}' },
          indicator: { value: '{colors.slate.400}' },
        },
        navigationBar: {
          background: { value: '#0C142E' },
          border: { value: 'rgba(255, 255, 255, 0.65)' },
          active: { value: '#FFFFFF' },
          inative: { value: '#D8D8DC' },
        },
        notifications: {
          title: { value: '#101828' },
          description: { value: '#364153' },
          link: { value: '#2563EB' },
          timestamp: { value: '{colors.slate.500}' },
          groupLabel: { value: '#6A7282' },
          unreadBg: { value: '#EFF4FF' },
          border: { value: 'rgba(0, 0, 0, 0.1)' },
        },
      },
    },
  },
  globalCss: {
    ':root': {
      '--app-font-family':
        '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    },
    'body': {
      fontFamily: 'var(--app-font-family)',
    },
    '*': {
      'borderColor': 'border.solid',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: '{colors.scrollbar.track}',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '{colors.scrollbar.thumb}',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '{colors.scrollbar.thumbHover}',
      },
    },
    '*::selection': {
      bg: 'info.subtle',
    },
    ':host, :host *': {
      fontFamily: 'var(--app-font-family, inherit)',
    },
    '[data-part="trigger"]': {
      '&[aria-selected="true"]::before': {
        '--indicator-color': '{colors.blue.600}',
        '--indicator-thickness': '4px',
        'borderRadius': '4px 4px 0 0',
      },
    },
  },
});

const customTheme = createSystem(defaultConfig, customConfig);

export default customTheme;
